import { Users, TrendingUp, Eye, Send, CalendarCheck } from 'lucide-react';
import { KPICard } from '../../shared/KPICard';
import type { FpOverviewData, AnalyticsQueryParams } from '../../../../types/analytics.types';

type FPKpiRowProps = {
  data: FpOverviewData;
  dateRange?: AnalyticsQueryParams['dateRange'];
};

const RANGE_LABELS: Record<string, string> = {
  '7d': 'last 7 days',
  '30d': 'last 30 days',
  '90d': 'last 90 days',
  '12m': 'last 12 months',
  custom: 'selected period',
};

/** How the previous comparison period reads for each range. */
const PREVIOUS_PERIOD_LABELS: Record<string, string> = {
  '7d': 'vs prev. 7 days',
  '30d': 'vs last month',
  '90d': 'vs prev. 90 days',
  '12m': 'vs prev. 12 months',
  custom: 'vs prev. period',
};

export const FPKpiRow = ({ data, dateRange }: FPKpiRowProps) => {
  const range = dateRange ?? '30d';
  // Both labels are derived from the selected range: the backend scopes these
  // values to it, so hardcoding "this month" / "vs last month" would lie on
  // every other range.
  const newAdvisorsLabel = `${data.newAdvisorsInRange} new in ${
    RANGE_LABELS[range] ?? 'selected period'
  }`;
  const engagementTrendLabel = `${Math.abs(data.advisorEngagementRateMoM)}% ${
    PREVIOUS_PERIOD_LABELS[range] ?? 'vs prev. period'
  }`;

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
      <KPICard
        title="Active Advisors"
        value={data.activeAdvisors.toLocaleString('en-US')}
        icon={Users}
        subtitle={newAdvisorsLabel}
      />
      <KPICard
        title="Advisor Engagement Rate"
        value={`${data.advisorEngagementRate}%`}
        icon={TrendingUp}
        trend={{
          value: engagementTrendLabel,
          isPositive: data.advisorEngagementRateMoM >= 0,
        }}
      />
      <KPICard
        title="Active Clients"
        value={data.activeClients.toLocaleString('en-US')}
        icon={Eye}
        subtitle="Care Portal logins"
      />
      <KPICard
        title="Client Referrals"
        value={data.clientReferrals.toLocaleString('en-US')}
        icon={Send}
        subtitle="To service providers"
      />
      <KPICard
        title="Consultations Booked"
        value={data.consultationsBooked.toLocaleString('en-US')}
        icon={CalendarCheck}
        subtitle="Requests + Calendly"
      />
    </div>
  );
};
